import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input } from '@arco-design/web-react';

import { DEFAULT_SLIDER_MARK_COUNT } from '@/components';

import { PluginModal } from '../../components/PluginModal';
import { buildDiskPricingInitPatch } from '../../components/diskPricingSync';
import { DEFAULT_DISK_SIZE, DiskVolumeFields, readDiskVolumeFormValues } from '../../components/diskVolumeFields';
import { isReclaimPolicyLocked, resolveReclaimPolicyValue } from '../../components/reclaimPolicyField';
import { normalizeVendorDeviceMeta, readDiskPricingMeta } from '../../components/vendorDevice';
import { OptionMetaRootField } from '../../platform/option-meta';
import { getModalPopupContainer } from '../../utils/modalPopupContainer';
import styles from './index.module.less';
import type { StorageDataDiskCreateModalProps } from './types';

enum Field {
  DISK_NAME = 'diskName',
  DISK_SIZE = 'diskSize',
  STORAGE_CLASS = 'storageClass',
  RECLAIM_POLICY = 'reclaimPolicy',
}

/** 新建 / 编辑数据盘弹窗（空白盘，无镜像） */
export function StorageDataDiskCreateModal({
  visible,
  form,
  title,
  initDisk,
  onOk,
  onCancel,
  billModelId,
  deploymentTypeId,
  paymentTiming,
  skuType,
  resourceEditMode,
  resourceCode,
}: StorageDataDiskCreateModalProps) {
  const { t } = useTranslation();
  const openInitializedRef = useRef(false);
  /** 自定义规格下单编辑已有盘：名称 / 选型只读，仅改容量 */
  const selectionReadOnly = Boolean(initDisk) && resourceEditMode === 'order-custom';
  /** SKU / 下单：隐藏回收策略并固定 Delete */
  const reclaimLocked = isReclaimPolicyLocked(resourceEditMode);

  useEffect(() => {
    if (!visible) {
      openInitializedRef.current = false;
      return;
    }
    if (openInitializedRef.current) return;
    openInitializedRef.current = true;

    form.resetFields();
    if (initDisk) {
      form.setFieldsValue({
        [Field.DISK_NAME]: initDisk.diskName,
        [Field.DISK_SIZE]: initDisk.diskSize ?? DEFAULT_DISK_SIZE,
        [Field.STORAGE_CLASS]: initDisk.storageClass,
        [Field.RECLAIM_POLICY]: resolveReclaimPolicyValue(initDisk.reclaimPolicy, reclaimLocked),
        ...buildDiskPricingInitPatch(initDisk.pricing),
      });
    } else {
      form.setFieldsValue({
        [Field.DISK_SIZE]: DEFAULT_DISK_SIZE,
        [Field.RECLAIM_POLICY]: resolveReclaimPolicyValue(undefined, reclaimLocked),
      });
    }
  }, [form, initDisk, reclaimLocked, visible]);

  const handleOk = async () => {
    try {
      await form.validate();
    } catch {
      return false;
    }
    await onOk();
  };

  const modalTitle = title ?? (initDisk ? t('components:edit.data.disk') : t('components:create.data.disk'));

  return (
    <PluginModal className={styles.createModal} visible={visible} title={modalTitle} focusLock={false} onCancel={onCancel} onOk={handleOk}>
      <div className={styles.modalBody}>
        <Form form={form} layout="vertical">
          <Form.Item
            field={Field.DISK_NAME}
            label={t('common:name')}
            disabled={selectionReadOnly}
            rules={[{ required: true, message: t('common:validate.not-empty') }]}
          >
            <Input placeholder={t('common:input.placeholder')} disabled={selectionReadOnly} />
          </Form.Item>
          <DiskVolumeFields
            form={form}
            diskSizeField={Field.DISK_SIZE}
            storageClassField={Field.STORAGE_CLASS}
            reclaimPolicyField={Field.RECLAIM_POLICY}
            enabled={visible}
            billModelId={billModelId}
            deploymentTypeId={deploymentTypeId}
            paymentTiming={paymentTiming}
            skuType={skuType}
            resourceEditMode={resourceEditMode}
            resourceCode={resourceCode}
            selectionReadOnly={selectionReadOnly}
            getPopupContainer={getModalPopupContainer}
            markCount={DEFAULT_SLIDER_MARK_COUNT}
          />
          <OptionMetaRootField />
        </Form>
      </div>
    </PluginModal>
  );
}

export function getStorageDataDiskCreateModalValues(
  form: StorageDataDiskCreateModalProps['form'],
  options?: { resourceEditMode?: string },
) {
  const values = form.getFieldsValue();
  const pricing = normalizeVendorDeviceMeta(readDiskPricingMeta(values as Record<string, unknown>));
  const diskVolume = readDiskVolumeFormValues(
    values as Record<string, unknown>,
    {
      diskSize: Field.DISK_SIZE,
      storageClass: Field.STORAGE_CLASS,
      reclaimPolicy: Field.RECLAIM_POLICY,
    },
    { lockReclaimPolicy: isReclaimPolicyLocked(options?.resourceEditMode) },
  );
  return {
    diskName: String(values[Field.DISK_NAME] ?? '').trim(),
    ...diskVolume,
    pricing,
  };
}
